import React, { useCallback, useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { ENROLL_REQUEST_STATE } from '../../../lib/models/enroll';
import { ENROLL_AUTH_REQUEST_TYPE } from '../../../lib/models/enroll-requests';
import { RootState } from '../../../lib/store';
import { enrollAuth } from '../../../lib/store/auth';
import { calculateElapsedTime } from '../../../lib/util/DateTimeHelpers';
import { useCounter } from '../../../lib/util/hooks/TimeoutHooks';

import EmailSentAce from '../../../assets/img/email-sent-ace.png';
import IconClose from '../../../assets/img/icon-close.svg';
import {
  GTM_CATEGORIES,
  GTM_CLIENT_CREATION_STEPS,
  GTM_EVENTS,
} from '../../../constants/gtm';
import { hashSHA256 } from '../../../util/hash';
import { ClickableDiv, ClickableImage } from '../../index';

import styles from './VerifyEmailModal.module.scss';

type Props = {
  forceNoRender?: boolean;
  noModalDisplay?: boolean;
  handleCloseVerifyEmailModal: () => void;
  userEmail: string;
  largeText?: boolean;
};
const SECONDS = 60;

let hasGtmEventFired = false;

const VerifyEmailModal = ({
  handleCloseVerifyEmailModal,
  forceNoRender,
  noModalDisplay,
  userEmail,
  largeText,
}: Props) => {
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.auth.enroll.userId);
  const confirmedStepsEmail = useSelector((state: RootState) => state.auth.enroll.confirmedSteps?.email);
  const enrollStatus = useSelector((state: RootState) => state.auth.enroll.status);
  const [isConfirmedEmail, setIsConfirmedEmail] = useState<boolean>(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(SECONDS);
  const [resetCount, setResetCount] = useState<number>(0);

  // Push GTM event: waits for auth state to load, then fires STEP_3 or STEP_4 (never both)
  useEffect(() => {
    if (hasGtmEventFired) return;
    if (enrollStatus !== ENROLL_REQUEST_STATE.READY) return;

    hasGtmEventFired = true;

    const step = confirmedStepsEmail
      ? GTM_CLIENT_CREATION_STEPS.STEP_4
      : GTM_CLIENT_CREATION_STEPS.STEP_3;

    const pushGtmEvent = async () => {
      const hashedEmail = await hashSHA256(userEmail);
      TagManager.dataLayer({
        dataLayer: {
          event: GTM_EVENTS.CLIENT_CREATION,
          event_step: step.step,
          event_category: GTM_CATEGORIES.CLIENT_CREATION_FUNNEL,
          event_action: step.action,
          event_label: step.label,
          client_id: userId,
          user_email_hashed: hashedEmail,
        },
      });
    };
    pushGtmEvent();
  }, [enrollStatus, confirmedStepsEmail, userEmail, userId]);

  const resendEmail = useCallback(() => {
    setRemainingSeconds(SECONDS);
    setResetCount(resetCount + 1);
    dispatch(
      enrollAuth({
        requestType: ENROLL_AUTH_REQUEST_TYPE.RESEND_EMAIL,
        email: userEmail,
      }),
    );
  }, [dispatch, userEmail, resetCount]);

  try {
    const channelVerifyEmailModalPage = new BroadcastChannel('confirmedEmail');
    channelVerifyEmailModalPage.addEventListener('message', (event) => {
      setIsConfirmedEmail(event.data);
    });
  } catch {
    console.error('BroadcastChannel is not supported by the browser');
  }

  const counterCallback = useCallback(
    (currentValue) => setRemainingSeconds(currentValue),
    [],
  );

  useCounter(
    counterCallback,
    1000,
    SECONDS,
    `VerifyEmailModal-${resetCount}`,
  );

  if (forceNoRender) {
    return <></>;
  }

  return (
    <div className={styles.wrapperContent}>
      {!noModalDisplay && (
      <h6 id="verifyEmailTitle" aria-label="verify email title">
        {lang.commonOnboardingEmailConfirmTitle()}
      </h6>
      )}
      {!isConfirmedEmail && !noModalDisplay && (
        <ClickableImage
          alt=""
          src={IconClose}
          className={styles.closeIcon}
          onClick={handleCloseVerifyEmailModal}
        />
      )}
      <img src={EmailSentAce} alt="aceemailimage" className={styles.image} />
      <div
        className={classNames(styles.text,
          { [styles.largeText]: largeText },
          { [styles.lightText]: noModalDisplay })}
        id="verificationText"
        aria-label="verification text"
      >
        {noModalDisplay && (
          <h6 id="verifyEmailTitle" aria-label="verify email title">
            {lang.commonOnboardingEmailConfirmTitle()}
          </h6>
        )}

        <span className={classNames(styles.text, { [styles.lightText]: noModalDisplay })}>
          <span className={styles.firstText}>{lang.commonOnboardingEmailConfirmDescription1()}</span>
          <span className={styles.userEmailText}>
            {`${userEmail}`}
          </span>
          {'. '}
          {lang.commonOnboardingEmailConfirmDescription2()}
        </span>
        <p className={classNames(styles.text, { [styles.lightText]: noModalDisplay })}>
          <span className={styles.firstText}>{lang.commonOnboardingEmailSpamDescription1()}</span>
          {'? '}
          <span className={styles.firstText}>{lang.commonOnboardingEmailSpamDescription2()}</span>
        </p>
        <p className={classNames(styles.text, { [styles.lightText]: noModalDisplay })}>
          <span className={styles.firstText}>{lang.commonOr()}</span>
        </p>
      </div>
      {isConfirmedEmail && (
      <p className={classNames(styles.text, { [styles.lightText]: noModalDisplay })}>
        {lang.commonOnboardingEmailAccountHasBeenConfirmed()}
      </p>
      )}
      {
        !isConfirmedEmail && (
        <div className={classNames(styles.text, { [styles.lightText]: noModalDisplay })} aria-label="haven't receive a confirmation email">
          {!noModalDisplay && lang.webSignInEmailNotReceived()}
          {
            remainingSeconds === 0
              ? (
                <ClickableDiv
                  id="sendAgain"
                  className={styles.link}
                  onClick={resendEmail}
                  aria-label="send again"
                >
                  {lang.webResendEmailAgain()}
                </ClickableDiv>
              )
              : (
                <div className={styles.timer}>
                  <div className={styles.sendAgain}>{lang.webResendEmail()}</div>
                  <div className={styles.remainingTime}>{calculateElapsedTime(remainingSeconds, 0)}</div>
                </div>
              )
          }
        </div>
        )
      }
    </div>
  );
};

export default VerifyEmailModal;
